import { signal, WritableSignal } from "@angular/core";
import { GetRowIdParams, GridApi, GridOptions } from "ag-grid-enterprise";

import { CategoryTableSubscription, TableSource } from "./category-table.subscription";
import { ChevronCellRenderer } from "@bitwarden/web-vault/app/components/tables/renderers/cell/end-icon-renderer/end-icon-renderer.component";
import { TableParent } from "@bitwarden/web-vault/app/models/types/table.types";
import { CategoryView } from "@bitwarden/web-vault/app/models/view/category/category.view";
import { CategoryItemRenderComponent } from "@bitwarden/web-vault/app/components/tables/renderers/cell/category-item-renderer/category-item-render.component";
import { CategorySourceRendererComponent } from "@bitwarden/web-vault/app/components/tables/renderers/cell/category-source-renderer/category-source-renderer.component";

export class CategoryTable extends CategoryTableSubscription {
  gridApi: GridApi;
  options: GridOptions<TableSource>;
  selectedCategory: WritableSignal<CategoryView> = signal<CategoryView>(null);

  constructor(parent: TableParent) {
    super();

    this.options = {
      context: { parent },
      rowSelection: "single",
      columnDefs: [
        {
          field: "name",
          cellRenderer: CategoryItemRenderComponent,
          filter: "agTextColumnFilter",
          headerName: "Category",
          flex: 3,
          minWidth: 150,
          cellStyle: {
            overflow: "hidden",
            whiteSpace: "normal",
            padding: "8px 0 8px 32px",
            display: "block",
          },
        },
        {
          field: "source",
          cellRenderer: CategorySourceRendererComponent,
          // Only the name cell takes focus; these columns hold nothing to operate.
          suppressNavigable: true,
          flex: 1,
          minWidth: 90,
          cellStyle: {
            overflow: "visible",
            padding: "8px 8px",
            display: "flex",
            alignItems: "center",
          },
        },
        {
          field: "rightChevron",
          cellRenderer: ChevronCellRenderer,
          suppressNavigable: true,
          flex: 0.3,
          minWidth: 52,
          maxWidth: 66,
          cellStyle: { overflow: "visible", padding: "8px 16px 8px 0" },
        },
      ],
      // The list is one Tab stop and the arrow keys move between rows (app-ag-grid-table's
      // tabToNextCell, #6246); Enter/Space run onRowClicked.
      suppressCellFocus: false,
      suppressContextMenu: true,
      onGridReady: (params) => {
        this.gridApi = params.api;
        this.onRowClicked(this.selectedCategory());
      },
      getRowId: (params: GetRowIdParams) => String(params.data.id),
      onRowClicked: (params) => {
        params.node.setSelected(true, false);
        this.onRowClicked(params.data);
      },
      // 64px only fit a single-line name; a wrapped 2-line name (~40px) plus the
      // transaction-count subtitle (~16px) plus the cell's own 16px vertical padding needs more.
      getRowHeight: () => 76,
      getRowStyle: () => ({ overflow: "visible", background: "transparent", border: "none" }),
      getRowClass: (params) => {
        if (params.node.group && params.node.level === 0) {
          return "tw-flex tw-items-end";
        }
        return "";
      },
    };
  }

  onRowClicked(category: CategoryView): void {
    if (category) {
      this.selectedCategory.set(category);
    }
  }
}
